/**
 * Um dashboard inteiro, embutido na tela.
 *
 * A diferença para o `dashboardCard`: aquele traz UM cartão, misturado com os
 * campos do formulário; este traz o dashboard inteiro, com as abas, o gridster e
 * a barra de filtro dele. Para misturar indicador com campo, o cartão é o certo;
 * este é para "esta tela, e o dashboard de vendas embaixo".
 *
 * **Isto já funcionava, de forma imperativa.** O `MainDashboardComponent` está
 * no registro do `DynamicLoaderService` e aceita `@Input() iddashboard`, então
 * `<component-factory [component]="'MainDashboardComponent'">` já montava um
 * dashboard em qualquer lugar. O que este renderer faz é tornar isso
 * declarativo, arrastável e versionado junto com a tela.
 *
 * Contrapartida registrada: o dashboard traz as próprias abas e o próprio
 * filtro, então é uma caixa com regras de navegação próprias dentro da tela.
 * Quem quiser um indicador ao lado de um campo quer o `dashboardCard`.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { MainDashboardModule } from '../../../system-view/main-dashboard/main-dashboard.module';
import { DashboardViewService } from '../../../services/dashboard.services';
import { UserAuthenticationServices } from '../../../services/user-authentication.services';
import { ScreenNode } from '../../model/node-props';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { TranslateTAPipe } from '../translate-ta.pipe';

@Component({
  selector: 'tem-dashboard-embed',
  standalone: true,
  imports: [CommonModule, MainDashboardModule, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Uma instância do serviço por dashboard vivo. O `main-dashboard` faz igual, e
  // o motivo está lá: o serviço guarda o contexto de empresa e contrato, e dois
  // dashboards compartilhando corrompem o contexto um do outro.
  providers: [DashboardViewService],
  template: `
    @if (dashboardId() > 0) {
      <div class="tem-embed" [style.min-height.px]="minHeight()">
        <main-dashboard [iddashboard]="dashboardId()"></main-dashboard>
      </div>
    } @else {
      <!-- Sem dashboard escolhido, o espaço é MARCADO. Um embutido vazio e um
           espaço em branco são indistinguíveis, e quem abre a tela não tem como
           saber que ali deveria haver um dashboard. -->
      <div class="tem-embed__empty">
        {{ 'builderScreen.dashboardEmbed.none' | translateTA }}
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .tem-embed { display: block; }

    .tem-embed__empty {
      padding: var(--tem-space-3);
      border: 1px dashed var(--tem-border);
      border-radius: var(--tem-radius);
      color: var(--tem-muted);
      text-align: center;
      font-size: var(--tem-fs-sm);
    }
  `]
})
export class DashboardEmbedRenderer implements NodeRenderer<'dashboardEmbed'> {

  readonly node = input.required<ScreenNode<'dashboardEmbed'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly dvs = inject(DashboardViewService);
  private readonly uas = inject(UserAuthenticationServices);

  readonly dashboardId = computed<number>(() => {
    const id = this.node().props.dashboardId;
    return typeof id === 'number' && id > 0 ? id : 0;
  });

  readonly minHeight = computed<number>(() => this.node().props.minHeight ?? 360);

  constructor() {
    // O contexto de empresa, contrato e usuário — o mesmo que o dashboard
    // resolve sozinho para os `PARAM_*` de ambiente.
    this.dvs.idUser = this.uas.user.iduser;
    this.dvs.codeCompanyList = this.uas.getCompanies();
    this.dvs.codeContractList = this.uas.getContracts();
    this.dvs.codeCompany = Number(this.uas.user.codecompany);
  }
}
